import Avatar from "components/core/Avatar";
import { useSelectedCompanyIssuer } from "components/financials/modern/data/useCompanyIssuers";
import getCompanyIssuerLinkProps from "components/financials/modern/getCompanyIssuerLinkProps";
import classNames from "components/ui/classNames";
import Link from "next/link";
import type { ISecurityCountry } from "types/graphqlTypes";

interface IssuerHeaderProps {
  title?: string;
  subtitle?: string;
  description?: string;
  country: ISecurityCountry;
  tickerSymbol: string;
  loading?: boolean;
  className?: string;
}
export default function IssuerHeaderByTickerSymbol({
  title,
  subtitle,
  description,
  country,
  tickerSymbol,
  className,
}: IssuerHeaderProps) {
  const { issuer } = useSelectedCompanyIssuer({
    country,
    tickerSymbol: tickerSymbol.toUpperCase(),
    exchangeId: null,
    vertical: null,
  });
  if (!issuer) {
    return null;
  }

  return (
    <header className={classNames("mx-auto flex max-w-7xl flex-col gap-4", className)}>
      <div className="space-y-2">
        <Link {...getCompanyIssuerLinkProps(issuer)} className="inline-flex items-center gap-x-2" target="_blank">
          <Avatar
            src={issuer.niceLogo?.url || null}
            hashValue={issuer._id}
            alt={issuer.name}
            size={42}
            squircle={true}
            className={classNames("md:inline-flex")}
          />

          <h1
            className={classNames(
              "border-b-2 border-b-transparent text-2xl font-black text-gray-900",
              "hover:border-b-black",
              "sm:text-4xl",
              "dark:text-white dark:hover:border-b-white",
              { "sm:text-3xl": title },
            )}
          >
            {title ? (
              <>
                {issuer.tickerSymbol.toUpperCase()} - {issuer.name} &bull; {title}
              </>
            ) : (
              <>
                {issuer.tickerSymbol.toUpperCase()} - {issuer.name}
              </>
            )}
          </h1>
        </Link>

        <p
          className={classNames(
            "line-clamp-4 text-sm leading-6 tracking-wide text-slate-600 dark:text-slate-400",
            "sm:line-clamp-none sm:text-base sm:leading-7",
          )}
        >
          {issuer.description}
        </p>

        {subtitle && description ? (
          <div className="pt-2">
            <h2 className={classNames("text-2xl font-black text-slate-800")}>{subtitle}</h2>
            <p className="tracking-wide text-slate-600">{description}</p>
          </div>
        ) : (
          <h2 className="font-medium tracking-wide text-slate-600">{description}</h2>
        )}
      </div>
    </header>
  );
}
